import React, { useState, useRef } from 'react'
import styled from 'styled-components'
import i18n from 'live/services/i18n'
import { WidgetEditorContext } from 'live/context/widget-editor'
import { IconAndColorConfigsContainer } from '../ChannelConfigs'

interface Props {
    label: string
    configKey: string
    configs: AlarmConfigs
    onChange: (configKey: string, newValue: AlarmConfigs) => void
}

export default function AlarmConfigBox(props: Props): React.ReactElement {
    const configs: AlarmConfigs = props.configs
        ? { ...props.configs }
        : {
              configured: {
                  background: '',
                  iconColor: 'green',
                  icon: 'check'
              },
              warning: {
                  background: '#E6E600',
                  iconColor: '#E6E600',
                  icon: 'exclamation',
                  blink: 1000
              },
              critical: {
                  background: '#CC2900',
                  iconColor: '#CC2900',
                  icon: 'exclamation',
                  blink: 1000
              },
              notMapped: {
                  background: 'blue',
                  iconColor: 'blue',
                  icon: 'times'
              }
          }[props.configKey]

    const isEditor = React.useContext<{ editor: string }>(WidgetEditorContext)?.editor
    const reference = useRef(null)
    const [open, setOpen] = useState(false)

    const toggleOpen = (): void => {
        reference.current.classList.remove('alarmConfigBoxOpen')
        setOpen(s => !s)
    }

    const changeChannelAlarmConfig = (configField: string, newValue: string): void => {
        const newConfigs: AlarmConfigs = { ...configs }
        newConfigs[configField] = newValue
        props.onChange(props.configKey, newConfigs)
    }

    return (
        <AlarmConfigBoxContainer open={open} isEditor={isEditor}>
            <div ref={reference} className={` alarmConfigBox ${open ? 'alarmConfigBoxOpen' : ''}`}>
                <header className={`alarmConfigBox--header`}>
                    <h3 className={`alarmConfigBox--header__heading`} onClick={toggleOpen}>
                        <button
                            className={`alarmConfigBox--header__heading--accordionBtn`}
                            aria-controls={'button|arual-controls'}
                            aria-label={`${i18n('Open alarm options for')} ${props.configKey ?? props.label}`}
                            aria-expanded={open}
                        >
                            <span className={`alarmConfigBox--header__heading--accordionBtn__title`}>
                                {props.label}
                            </span>
                            <i
                                role="channel-config-toggler"
                                className={`fas color-primary fa-angle-${
                                    open ? 'up' : 'down'
                                } alarmConfigBox--header__heading--accordionBtn__chevron`}
                            />
                        </button>
                    </h3>
                </header>
                <div className={`alarmConfigBox--alarmConfigs`}>
                    {open ? (
                        <IconAndColorConfigsContainer
                            icon={configs.icon}
                            iconConfigKey={'icon'}
                            iconColor={configs.iconColor}
                            iconColorConfigKey={'iconColor'}
                            backgroundColor={configs.background}
                            backgroundConfigKey={'background'}
                            onChange={changeChannelAlarmConfig}
                            blink={(props.configKey === 'critical' || props.configKey === 'warning') && configs.blink}
                            textFieldHeight="28px"
                            isEditor={Boolean(isEditor)}
                        />
                    ) : null}
                </div>
            </div>
        </AlarmConfigBoxContainer>
    )
}

interface IContainerProps {
    open: boolean
    isEditor?: string
}
const AlarmConfigBoxContainer = styled.div<IContainerProps>`
    --buttonHeight: 28px;
    margin: 7px 0 0 0;
    border: solid;
    border-color: ${({ theme, isEditor }): string => (isEditor ? theme['gray-lighter'] : theme['darker-bg'])};
    border-width: 1px;
    border-radius: 3px;
    .fa-chart-bar {
        font-size: 15px;
    }
    .alarmConfigBox {
        overflow: visible;
        max-height: var(--buttonHeight);
        /* background-color: ${({ theme }): string => theme['dash-background']}; */
        &--alarmConfigs {
            padding-bottom: 7px;
        }
        &.alarmConfigBoxOpen {
            max-height: 100%;
        }
        &--header {
            border-radius: ${({ open }) => (!open ? '3px' : 'unset')};
            border-top-left-radius: 3px;
            border-top-right-radius: 3px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            height: var(--buttonHeight);
            &__heading {
                flex: 1;
                &--accordionBtn {
                    padding: 7px;
                    border: none;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    width: 100%;
                    color: ${({ theme }) => theme['link-color']};
                    background: none;
                    &__title {
                        flex: 1;
                        margin-left: 7px;
                        color: ${({ theme, isEditor }) => (isEditor ? theme['main-tipography'] : 'white')};
                        font-size: 13px;
                        line-height: normal;
                        text-align: left;
                    }
                    &__chevron {
                        font-size: 14px;
                        margin-right: 10px;
                        cursor: pointer;
                    }
                }
            }
        }
    }
`
